// ---------------------------------------------------------------------------
// Test Mode — the demo user's reminders.
//
// Held in a mutable module-level list, not rebuilt on every call: the page
// creates, snoozes, completes and deletes reminders, and a demo where the
// "Finalizat" button does nothing reads as broken. The list resets on reload,
// which is exactly what a demo wants.
// ---------------------------------------------------------------------------

import type { Reminder, ReminderPatch } from '../lib/remindersApi';

function at(offsetMinutes: number): string {
  return new Date(Date.now() + offsetMinutes * 60_000).toISOString();
}

function seed(): Reminder[] {
  const now = new Date().toISOString();
  return [
    {
      id: 'rem-1',
      title: 'Sună clientul FashionRO pentru feedback pe checkout',
      description: 'Au testat noul flux de plată ieri; vezi dacă au blocaje înainte de release.',
      dueAt: at(25),
      estimatedMinutes: 15,
      done: false,
      doneAt: null,
      ackAt: null,
      createdAt: now,
    },
    {
      id: 'rem-2',
      title: 'Trimite oferta pentru Integrare ERP',
      description: 'IndustriaPlus așteaptă oferta revizuită cu fazele 1–2.',
      dueAt: at(3 * 60),
      estimatedMinutes: 45,
      done: false,
      doneAt: null,
      ackAt: null,
      createdAt: now,
    },
    {
      id: 'rem-3',
      title: 'Verifică factura FinScale GmbH',
      description: null,
      dueAt: at(-2 * 60),
      estimatedMinutes: 10,
      done: false,
      doneAt: null,
      // Fired two hours ago and was dismissed, so the banner is not on screen
      // the moment the demo opens — one live reminder (rem-1) is enough.
      ackAt: at(-110),
      createdAt: now,
    },
    {
      id: 'rem-4',
      title: 'Pregătește demo-ul pentru Mobile Banking App',
      description: 'Slide-uri + build de test pe TestFlight.',
      dueAt: at(24 * 60 + 30),
      estimatedMinutes: 90,
      done: false,
      doneAt: null,
      ackAt: null,
      createdAt: now,
    },
    {
      id: 'rem-5',
      title: 'Reînnoiește certificatul SSL pentru app.firmio',
      description: 'Expiră săptămâna viitoare.',
      dueAt: at(3 * 24 * 60),
      estimatedMinutes: 20,
      done: false,
      doneAt: null,
      ackAt: null,
      createdAt: now,
    },
    {
      id: 'rem-6',
      title: 'Plătește abonamentul AWS',
      description: null,
      dueAt: at(-26 * 60),
      estimatedMinutes: 5,
      done: true,
      doneAt: at(-25 * 60),
      ackAt: at(-26 * 60),
      createdAt: now,
    },
  ];
}

let reminders: Reminder[] | null = null;

function list(): Reminder[] {
  if (!reminders) reminders = seed();
  return reminders;
}

export function buildReminders(): { reminders: Reminder[]; serverNow: string } {
  return { reminders: [...list()], serverNow: new Date().toISOString() };
}

/** Mutations the Reminders page and the banner perform, applied in memory. */
export function mockReminderWrite(
  pathname: string,
  method: string,
  body: unknown,
): { reminder?: Reminder; id?: string; deleted?: boolean } {
  const items = list();
  const id = pathname.split('/')[3];
  const input = (body ?? {}) as Partial<ReminderPatch> & {
    title?: string;
    description?: string | null;
    dueAt?: string;
    estimatedMinutes?: number | null;
  };

  if (method === 'POST') {
    const reminder: Reminder = {
      id: `rem-${Date.now()}`,
      title: input.title ?? '',
      description: input.description ?? null,
      dueAt: input.dueAt ?? new Date().toISOString(),
      estimatedMinutes: input.estimatedMinutes ?? null,
      done: false,
      doneAt: null,
      ackAt: null,
      createdAt: new Date().toISOString(),
    };
    items.unshift(reminder);
    return { reminder };
  }

  const idx = items.findIndex((r) => r.id === id);
  if (method === 'DELETE') {
    if (idx !== -1) items.splice(idx, 1);
    return { id, deleted: true };
  }
  if (idx === -1) return {};
  const current = items[idx]!;
  const next: Reminder = { ...current };
  if (input.title !== undefined) next.title = input.title;
  if (input.description !== undefined) next.description = input.description;
  if (input.estimatedMinutes !== undefined) next.estimatedMinutes = input.estimatedMinutes;
  // Same rule as the server: moving the due moment re-arms the banner.
  if (input.dueAt !== undefined) {
    next.dueAt = input.dueAt;
    next.ackAt = null;
  }
  if (input.ack) next.ackAt = new Date().toISOString();
  if (input.done !== undefined) {
    next.done = input.done;
    next.doneAt = input.done ? new Date().toISOString() : null;
  }
  items[idx] = next;
  return { reminder: next };
}
